import React from 'react';
import { Search, FileSearch, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { fmtMoney } from '../helpers';
import { C, tabular } from '../styles';
import type { Line, Comparison } from '../types';

interface Props {
  line: Line;
  comparison: Comparison | null;
  onEdit: (employeeId: string | number) => void;
  onCompare: (line: Line) => void;
}

const ROW_COLS = '0.9fr 100px 120px 200px 130px 220px 160px 90px';

export default function EmployeeRow({ line, comparison, onEdit, onCompare }: Props) {
  const compData = comparison?.employees?.[String(line.stub_id)];
  const hasComparison = Boolean(compData && compData.has_comparison);

  const renderCompare = (): React.ReactNode => {
    if (!hasComparison || !compData) return null;
    const isDown = compData.direction === 'down';
    const isFlat = compData.direction === 'flat';
    const Icon = isFlat ? Minus : (isDown ? ArrowDown : ArrowUp);
    const label = isFlat ? 'No change' : ((isDown ? 'Down ' : 'Up ') + compData.percent + '%');
    return (
      <>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 14, fontWeight: 500, color: C.ink,
        }}>
          <Icon size={16} strokeWidth={2} />
          {label}
        </span>
        <button
          onClick={() => onCompare(line)}
          title={'Compare ' + (line.name || 'employee') + ' to last payday'}
          aria-label={'Compare ' + (line.name || 'employee') + ' to last payday'}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = C.brandBg;
            (e.currentTarget as HTMLButtonElement).style.color = C.brandDark;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = C.muted;
          }}
          style={{
            background: 'transparent', border: 'none',
            padding: 4, borderRadius: 6, cursor: 'pointer',
            color: C.muted, display: 'inline-flex', alignItems: 'center',
          }}
        >
          <FileSearch size={16} strokeWidth={2} />
        </button>
      </>
    );
  };

  return (
    <div style={{
      padding: '16px 0',
      display: 'grid',
      gridTemplateColumns: ROW_COLS,
      gap: 12, alignItems: 'center',
      borderBottom: '1px solid ' + C.line,
      fontSize: 13.5, color: C.ink, ...tabular,
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{line.name}</div>
        <div style={{ fontSize: 11.5, color: C.ink, marginTop: 2, fontWeight: 500 }}>
          {line.classification} · {fmtMoney(line.hourly_rate)}/hr
          {line.includes_stat && ' · includes stat'}
        </div>
      </div>
      <div style={{ textAlign: 'right', fontWeight: 500 }}>{line.total_hours}h</div>
      <div style={{ textAlign: 'right', fontWeight: 500 }}>{fmtMoney(line.gross_pay)}</div>
      <div style={{ textAlign: 'right', fontWeight: 500 }}>{fmtMoney(line.employee_taxes)}</div>
      <div style={{
        textAlign: 'right', fontWeight: 700, fontSize: 14,
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
      }}>
        {fmtMoney(line.net_pay)}
        <button
          onClick={() => onEdit(line.employee_id)}
          title="View or edit paycheque"
          aria-label="View or edit paycheque"
          style={{
            background: C.card, border: '1px solid ' + C.line, borderRadius: 6,
            padding: '3px 5px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', color: C.ink,
          }}
        >
          <Search size={12} strokeWidth={2.5} />
        </button>
      </div>
      <div style={{ textAlign: 'right', fontWeight: 500 }}>{fmtMoney(line.employer_taxes)}</div>
      <div style={{
        textAlign: 'right',
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
      }}>
        {renderCompare()}
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{
          fontSize: 10.5, color: C.ink, fontWeight: 700,
          padding: '3px 7px', background: C.page, borderRadius: 5,
          textTransform: 'uppercase',
        }}>
          {line.payment_method}
        </span>
      </div>
    </div>
  );
}

export { ROW_COLS };